import React, { useEffect } from "react";
import { useCodesSociete } from "utils/useGetCodeSociete";
import { Input, InputConformProps } from "./Input";
import { Autocomplete, Box, Typography } from "@mui/material";
import { CodeSociete } from "api/codesSocietes";
import { FieldMetadata, getInputProps } from "@conform-to/react";
import { useMemo, useState } from "react";
import { regexCodeSociete, regexCodeSocieteInput } from "utils/regex";
import { useTranslation } from "react-i18next";
import { CHAMP_REQUIS, MESSAGE_ERROR_FORMAT_CODE_SOCIETE } from "utils/constantes";
import { useGetHabilitation } from "utils/useGetHabilitation";
import { useUser } from "utils/useGetUser";
import { Perimetre, Role } from "api/auth";
import { uniqBy } from "utils/uniqBy";

type CodeSocieteInputProps = Omit<InputConformProps, "onChange"> & {
  defaultValue?: any;
  type: "assureur" | "courtier" | "GPSA";
  nomCodeSociete?: string;
  onChange?: (codeSociete: CodeSociete) => void;
  onInputChange?: (e: any, value: string) => void;
  autocompleteKey?: string;
  customMessageSocieteInconnu?: string;
  customMessageErrorFormat?: string;
  dontShowErrorMessage?: boolean;
};

type CodeSocieteInputWithOptionsProps = Omit<CodeSocieteInputProps, "type"> & {
  perimetres?: Perimetre[];
  actif?: boolean;
};

const typeByLabel = {
  assureur: "A",
  courtier: "I",
};

export type CurrentSociete = {
  code?: string;
  nom?: string;
};

export function CodeAssureurHabiliteInput({
  perimetres,
  actif,
  ...props
}: CodeSocieteInputWithOptionsProps) {
  const user = useUser();

  const { habilitations } = useGetHabilitation(
    user.role === Role.GPSA ? undefined : user?.codeSociete,
    perimetres,
    undefined,
    actif,
  );

  const options = useMemo(() => {
    const mapped = (habilitations ?? []).map((h) => ({
      id: h.idHabilitation as unknown as number,
      type: "A",
      code: h.codeAssureur,
      nom: h.nomAssureur,
      label: h.codeAssureur,
    }));

    const unique = uniqBy(mapped, "code");

    return unique.sort((a, b) => {
      const aEmpty = !a.nom;
      const bEmpty = !b.nom;

      // noms vides en bas
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;

      if (aEmpty) {
        return a.code.localeCompare(b.code, "fr", { sensitivity: "base" });
      }

      const byName = a.nom!.localeCompare(b.nom!, "fr", { sensitivity: "base" });
      if (byName !== 0) return byName;

      return a.code.localeCompare(b.code, "fr", { sensitivity: "base" });
    });
  }, [habilitations]);

  return (
    <CodeSocieteInputWithOptions
      options={options}
      type="assureur"
      customMessageSocieteInconnu="societe.AUCUNE_HABILITATION_CONNUE"
      {...props}
    />
  );
}

export function CodeIntermediairesHabiliteInput({
  perimetres,
  actif,
  ...props
}: CodeSocieteInputWithOptionsProps) {
  const user = useUser();

  const { habilitations } = useGetHabilitation(
    undefined,
    perimetres,
    user.role === Role.GPSA ? undefined : user?.codeSociete,
    actif,
  );

  const options = useMemo(() => {
    const mapped = (habilitations ?? []).map((h) => ({
      id: h.idHabilitation as unknown as number,
      type: "A",
      code: h.codeGestionnaire,
      nom: h.nomIntermediaire,
      label: h.codeGestionnaire,
    }));

    const unique = uniqBy(mapped, "code");

    return unique.sort((a, b) => {
      const aEmpty = !a.nom;
      const bEmpty = !b.nom;

      // noms vides en bas
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;

      if (aEmpty) {
        return a.code.localeCompare(b.code, "fr", { sensitivity: "base" });
      }

      const byName = a.nom!.localeCompare(b.nom!, "fr", { sensitivity: "base" });
      if (byName !== 0) return byName;

      return a.code.localeCompare(b.code, "fr", { sensitivity: "base" });
    });
  }, [habilitations]);

  return (
    <CodeSocieteInputWithOptions
      options={options}
      // options={[{ id: 1, code: "12345678", nom: "Assureur A", type: "I", label: "Assureur A" }]} // TODO: Replace with actual options when the API is ready
      type="assureur"
      {...props}
    />
  );
}

export function CodeSocieteInput({ type, ...props }: CodeSocieteInputProps) {
  const codeSociete = useCodesSociete();
  const options = useMemo(() => {
    return (
      codeSociete
        .filter((c) => (type === "GPSA" ? true : c.type === typeByLabel[type]))
        .map((c) => ({ ...c, label: c.code })) ?? []
    );
  }, [codeSociete, type]);
  return <CodeSocieteInputWithOptions options={options} type={type} {...props} />;
}

export function CodeSocieteInputWithOptions({
  label = "Code Société",
  disabled,
  type,
  field,
  onChange,
  onInputChange,
  autocompleteKey,
  options,
  nomCodeSociete,
  customMessageErrorFormat,
  ...props
}: CodeSocieteInputProps & { options: CodeSociete[] }) {
  const defaultValue = useMemo(() => {
    return props.defaultValue ?? field?.value ?? "";
  }, [props.defaultValue, field?.value]);

  const [currentSociete, setCurrentSociete] = useState<CurrentSociete | null>({
    code: defaultValue,
    nom: options.find((c) => c.code === defaultValue)?.nom,
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(
    field?.errors ? field.errors[0] : null,
  );

  useEffect(() => {
    setErrorMessage(field?.errors ? field.errors[0] : null);
  }, [field?.errors]);

  const [isResolvingInitialValue, setIsResolvingInitialValue] = useState<boolean>(!!defaultValue);

  // TODO : Nécessaire pour mettre à jour le currentSociete lorsque les options arrivent (pcq il y a d'abord une requete vers le back avec CodeAssureurHabiliteInput)
  // A voir si on peut le faire autrement
  useEffect(() => {
    setCurrentSociete({
      code: defaultValue,
      nom: options.find((c) => c.code === defaultValue)?.nom,
    });
    setIsResolvingInitialValue(false);
  }, [defaultValue, options]);

  useEffect(() => {
    if (inputValue !== defaultValue) {
      setInputValue(defaultValue ?? "");
    }
  }, [defaultValue]);

  const [inputValue, setInputValue] = useState<string>(defaultValue ?? "");

  console.log(inputValue, defaultValue);

  return (
    <Autocomplete
      // key={autocompleteKey ?? "autocompleteKey"}
      freeSolo
      disabled={disabled}
      disableClearable={props.tableHeadStyle ? true : false}
      options={options}
      size="small"
      inputValue={inputValue}
      sx={{
        width: props.fullWidth ? "100%" : "300px",
        ...(props.tableHeadStyle && { "& .MuiAutocomplete-inputRoot": { padding: "7px 4px" } }),
      }}
      filterOptions={(x, state) => {
        const searchValue = String(state.inputValue).toLowerCase();
        return x.filter(
          (i) =>
            String(i.code).toLowerCase().includes(searchValue) ||
            String(i.nom).toLowerCase().includes(searchValue),
        );
      }}
      onChange={(_, value) => {
        // Si le type est string => l'utilisateur a appuyé sur la touche 'Entrer'
        // Ne rien faire car déjà géré par onInputChange
        if (typeof value === "string") {
          return;
        }
        // Sinon (type CodeSociete) => l'utilisateur a sélectionné une société dans la liste
        const codeSociete = value as CodeSociete;

        if (value != null) {
          setCurrentSociete(codeSociete);
          setErrorMessage(null); // Clear error si la valeur saisie est valide
        } else {
          setCurrentSociete(null);
          // Clear error (ou affichier) si la valeur est vide
          if (props.required) {
            setErrorMessage(CHAMP_REQUIS);
          } else {
            setErrorMessage(null);
          }
        }

        if (onChange) {
          onChange(codeSociete);
        }
      }}
      onInputChange={(e, value) => {
        if (regexCodeSociete.test(value)) {
          setErrorMessage(null); // Clear error si la valeur saisie est valide
          if (currentSociete?.code !== value) {
            if (value.length === 8) {
              setCurrentSociete({ code: value, nom: options.find((c) => c.code === value)?.nom });
            } else if (currentSociete != null) {
              setCurrentSociete(null);
            }
          }
          setInputValue(value);
        } else if (regexCodeSocieteInput.test(value)) {
          if (value.length === 0) {
            // Clear error (ou affichier) si la valeur est vide
            if (props.required) {
              setErrorMessage(CHAMP_REQUIS);
            } else {
              setErrorMessage(null);
            }
          } else {
            // afficher message d erreur si le code societe renseigné et ne respecte pas la regex
            setErrorMessage(customMessageErrorFormat ?? MESSAGE_ERROR_FORMAT_CODE_SOCIETE);
          }
          setCurrentSociete(null);
          setInputValue(value);
        }

        if (onInputChange) {
          onInputChange(e, value);
        }
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
          {...props}
        >
          <Typography width="100%" fontWeight="bold">
            {option.code}
          </Typography>
          <Typography fontSize={12} width="100%">
            {option.nom}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <InputConformCodeSociete
          {...params}
          label={label}
          societeName={currentSociete?.nom}
          field={field}
          errorMessage={errorMessage}
          isEmpty={inputValue === ""}
          dontShowErrorMessage={props.dontShowErrorMessage || isResolvingInitialValue}
          {...props}
        />
      )}
    />
  );
}

export function InputConformCodeSociete({
  label,
  field,
  societeName,
  errorMessage,
  customMessageSocieteInconnu,
  isEmpty,
  dontShowErrorMessage,
  ...props
}: {
  label: any;
  field: FieldMetadata<any, any, any>;
  societeName: string | undefined;
  errorMessage?: string | null;
  customMessageSocieteInconnu?: string | null;
  isEmpty?: boolean;
  dontShowErrorMessage?: boolean;
}) {
  const { t } = useTranslation("common");

  const copyProps = props as any;
  const messageSocieteInconnu = customMessageSocieteInconnu
    ? t(customMessageSocieteInconnu)
    : t("societe.SOCIETE_NON_RECONNUE_RIE_AVEC_LABEL", {
        label: label === "" ? t("societe.codeSociete") : label,
      });

  const showError = !dontShowErrorMessage && errorMessage && (!isEmpty || copyProps.required);
  return (
    <Box sx={{ position: "relative" }}>
      <Input
        label={label}
        {...getInputProps(field, { type: "text" })}
        // Afficher les erreurs seulement si la valeur saisie n'est pas vide
        error={showError}
        errors={showError ? [t(errorMessage)] : []}
        data-cy={field.name}
        {...props}
      />
      <Box
        sx={{
          position: "absolute",
          left: 90,
          top: 0,
          height: "100%",
          width: "calc(100% - 100px)",
          display: "flex",
          alignItems: "flex-start",
          p: 0.5,
          pointerEvents: "none",
        }}
      >
        {societeName && !isEmpty && (
          <Typography
            fontSize={16}
            color={copyProps?.disabled ? "text.disabled" : "primary"}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              maxWidth: "100%",
              maxHeight: "100%",
              mt: copyProps?.tableHeadStyle ? 0.5 : 2,
              textOverflow: "ellipsis",
              ...(copyProps?.tableHeadStyle && {
                color: "white",
                fontSize: 12,
              }),
            }}
          >
            {": " + societeName}
          </Typography>
        )}
      </Box>
      {!dontShowErrorMessage && !societeName && !isEmpty && !errorMessage && (
        <Box>
          <Typography
            sx={{ color: "warning.main", fontWeight: "bold", marginLeft: "14px", fontSize: "12px" }}
          >
            {messageSocieteInconnu}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
